/**
 * Tenant user management client (§7.1).
 *
 * Every call here requires TENANT_ADMIN server-side; the UI hides the entry point for anyone else,
 * but the server is what actually enforces it.
 */

import { AuthError } from './auth';
import { authorizedFetch } from './cognito';
import { Role } from './types';

const API_BASE = '/api/v1';

export interface TenantUser {
  id: string;
  email: string;
  full_name: string;
  roles: Role[];
  status: 'ACTIVE' | 'DISABLED';
  last_seen_at?: string;
  can_read_phi: boolean;
}

/**
 * Every other client in the app goes through `authorizedFetch`; this one did not (§13.6 A8).
 *
 * That meant admin calls had no request timeout — so a stalled connection hung indefinitely with
 * no error — and no silent token refresh, so a token expiring mid-session redirected the tenant
 * admin through Cognito and lost whatever they were editing, in a screen where the rest of the
 * app would simply have carried on.
 */
async function call<T>(path: string, init: RequestInit = {}): Promise<T> {
  const res = await authorizedFetch(`${API_BASE}${path}`, {
    ...init,
    headers: {
      ...(init.body ? { 'Content-Type': 'application/json' } : {}),
      ...(init.headers ?? {}),
    },
  });
  if (res.status === 401) {
    // `authorizedFetch` has already tried a silent refresh and, failing that, begun the redirect.
    throw new AuthError(401, 'Not signed in');
  }
  if (!res.ok) {
    let message = `Request failed (${res.status})`;
    try {
      const body = await res.json();
      if (typeof body?.detail === 'string') message = body.detail;
    } catch {
      /* keep the generic message */
    }
    throw new AuthError(res.status, message);
  }
  return res.status === 204 ? (undefined as T) : (res.json() as Promise<T>);
}

export const fetchTenantUsers = () => call<TenantUser[]>('/tenant/users');

export const createTenantUser = (email: string, fullName: string, roles: Role[]) =>
  call<TenantUser>('/tenant/users', {
    method: 'POST',
    body: JSON.stringify({ email, full_name: fullName, roles }),
  });

export const updateTenantUserRoles = (id: string, roles: Role[]) =>
  call<TenantUser>(`/tenant/users/${id}`, { method: 'PATCH', body: JSON.stringify({ roles }) });

export const disableTenantUser = (id: string) =>
  call<TenantUser>(`/tenant/users/${id}/disable`, { method: 'POST' });

export const enableTenantUser = (id: string) =>
  call<TenantUser>(`/tenant/users/${id}/enable`, { method: 'POST' });

export const ALL_ROLES: Role[] = ['TENANT_ADMIN', 'TECHNICIAN', 'PHYSICIAN', 'CORPUS_REVIEWER'];

export const ROLE_LABEL: Record<Role, string> = {
  PLATFORM_OPS: 'Platform Ops',
  TENANT_ADMIN: 'Admin',
  TECHNICIAN: 'Technician',
  PHYSICIAN: 'Physician',
  CORPUS_REVIEWER: 'Corpus Reviewer',
  READ_ONLY: 'Read-only',
};


// ── Platform operations (§9.7) ─────────────────────────────────────────────────────────────────
//
// Managing customer organisations. Only PLATFORM_OPS reaches any of this, and the server is what
// enforces that — the UI hides the entry point, which is convenience rather than a control.

export type TenantStatus = 'ACTIVE' | 'SUSPENDED' | 'OFFBOARDING';

export interface TenantSummary {
  id: string;
  slug: string;
  name: string;
  jurisdiction: string;
  residency_region: string;
  status: TenantStatus;
  is_platform_operator: boolean;
  sla_hours: number;
  idle_logout_seconds: number;
  separation_of_duties_required: boolean;
  user_count: number;
  /** Studies ever registered, from the atomic per-tenant counter — not a live count. */
  studies_registered: number;
}

export interface PlatformAuditEntry {
  id: string;
  ts: string;
  action: string;
  actor_email: string | null;
  target_tenant_slug: string | null;
  before_state: Record<string, unknown> | null;
  after_state: Record<string, unknown> | null;
  reason: string | null;
}

export const listTenants = () => call<TenantSummary[]>('/platform/tenants');

export const createTenant = (body: {
  slug: string;
  name: string;
  jurisdiction?: string;
  sla_hours?: number;
  idle_logout_seconds?: number;
  separation_of_duties_required?: boolean;
}) => call<TenantSummary>('/platform/tenants', { method: 'POST', body: JSON.stringify(body) });

export const updateTenant = (
  id: string,
  body: Partial<Pick<TenantSummary,
    'name' | 'sla_hours' | 'idle_logout_seconds' | 'separation_of_duties_required'>>,
) => call<TenantSummary>(`/platform/tenants/${id}`, { method: 'PATCH', body: JSON.stringify(body) });

/** Suspension and reactivation. There is no delete — see the endpoint's own docstring. */
export const setTenantStatus = (id: string, status: TenantStatus, reason: string) =>
  call<TenantSummary>(`/platform/tenants/${id}/status`, {
    method: 'POST',
    body: JSON.stringify({ status, reason }),
  });

export const listTenantUsers = (id: string) =>
  call<TenantUser[]>(`/platform/tenants/${id}/users`);

/**
 * The first administrator of an organisation — the one account that cannot be created from
 * inside it. Always TENANT_ADMIN, which carries no PHI access: a platform operator cannot mint
 * clinical access in a customer's tenant, and the server ignores any roles sent here.
 */
export const provisionTenantAdmin = (id: string, body: {
  email: string; full_name: string; reason: string;
}) => call<TenantUser>(`/platform/tenants/${id}/admins`, {
  method: 'POST', body: JSON.stringify(body),
});

export const listPlatformAudit = () => call<PlatformAuditEntry[]>('/platform/audit');
